// Assembles the "trustlines this wallet still needs" list.
//
// Ctrl-Alt calls the XRPL trustline an "opt-in": a line from the investor's
// broker-custodied wallet to the Ctrl-Alt issuer wallet for one property's
// token. Until it exists the issuer cannot deliver, and the order sits at
// `awaiting_external_opt_in`.
//
// A row appears here when BOTH halves agree that a trustline has to be opened:
//
//   1. We hold no usable record in `investor_crypto_wallet_trustlines` for that
//      property — no row at all, or one that failed or was removed.
//   2. The VAISP confirms it from their side by parking an order at
//      `awaiting_external_opt_in`, which is what a pending opt-in row IS.
//
// Both halves, not either. Our own table alone would surface properties the
// vendor is perfectly happy with; the vendor alone would surface properties we
// already hold a line for. The pair is the only state an operator can act on
// from this screen.
//
// What this list deliberately does NOT do is reason about orders. It used to
// sweep the investor's whole order history and raise a card for every property
// they had ever ordered into, which dragged order references, order counts,
// history truncation and a per-property offering lookup onto a screen about
// wallets. Order state belongs on the orders screen. Here a property is a
// trustline that is missing, or it is not on the list.
//
// Token identity comes from the payload this screen already loads — the pending
// opt-in first (it joins `vasp_offerings` directly), then any trustline row for
// the same property. A property neither can describe still appears, with the gap
// named on the card rather than silently dropped: an offering that was never
// registered with the VAISP, or one carrying no issuer or currency code, is
// exactly the case an operator has to escalate.

import { TRUSTLINE_STATUS, type TrustlineStatus } from '../constants/trustlineStatus'
import type {
  TrustlineGap,
  WalletManagementPendingOptIn,
  WalletManagementTrustline,
} from '../types/cryptoWalletTypes'

/**
 * How alive a trustline row is, used to collapse several rows for one property
 * down to the one that decides what the card offers. A property that failed once
 * and was then trusted successfully is trusted — highest rank wins.
 */
const TRUSTLINE_LIVENESS: Record<string, number> = {
  [TRUSTLINE_STATUS.ACTIVE]: 3,
  [TRUSTLINE_STATUS.PENDING]: 2,
  [TRUSTLINE_STATUS.FAILED]: 1,
  [TRUSTLINE_STATUS.REMOVED]: 0,
}

/**
 * Whether the wallet's record for this property is one an operator still has to
 * act on — the first half of the two-part rule at the top of this file.
 *
 * `active` is a working record: the wallet holds the token and nothing on this
 * screen can improve that. `pending` is a record too, but an unfinished one — the
 * TrustSet was submitted and the ledger has not confirmed it, so the row stays
 * with a status check rather than a second submit. `failed` and `removed` leave
 * the wallet holding nothing, which is the same position as having no row at all.
 */
function needsTrustlineWork(status: string | null | undefined): boolean {
  return status !== TRUSTLINE_STATUS.ACTIVE
}

/**
 * Setting one up is the action only when there is no record or the last one
 * ended badly. A `pending` line is already in flight — a second TrustSet would
 * do nothing, so those rows offer the ledger re-read instead.
 *
 * Mirrors admin-service's `TRUSTLINE_STATES_NEEDING_SETUP`, which decides the
 * same thing for the `requiresTrustlineSetup` flag on the wire.
 */
function requiresSetup(status: string | null | undefined): boolean {
  return !status || status === TRUSTLINE_STATUS.FAILED || status === TRUSTLINE_STATUS.REMOVED
}

// Labels rather than field names: this list is read out on the card, and
// "offering" is what an operator would go looking for, not "offeringId".
const MISSING_FIELD_LABEL = {
  offeringId: 'offering',
  tokenHex: 'token currency code',
  issuerWalletAddress: 'issuer address',
} as const

type TokenIdentity = Pick<
  TrustlineGap,
  'offeringId' | 'offeringTitle' | 'tokenSymbol' | 'tokenHex' | 'issuerWalletAddress'
>

const EMPTY_TOKEN_IDENTITY: TokenIdentity = {
  offeringId: '',
  offeringTitle: '',
  tokenSymbol: '',
  tokenHex: '',
  issuerWalletAddress: '',
}

/**
 * Which of the three fields a TrustSet needs are absent.
 *
 * `tokenSymbol` is not among them — it is display sugar. The setup call is keyed
 * by offering, and the issuer/currency pair is what an operator verifies before
 * trusting it, so those three are what "token data available" means here.
 */
export function missingTokenFields(identity: TokenIdentity): string[] {
  const missing: string[] = []
  if (!identity.offeringId) missing.push(MISSING_FIELD_LABEL.offeringId)
  if (!identity.tokenHex) missing.push(MISSING_FIELD_LABEL.tokenHex)
  if (!identity.issuerWalletAddress) missing.push(MISSING_FIELD_LABEL.issuerWalletAddress)
  return missing
}

// Later sources only fill blanks — the first source to know a field wins. The
// caller sets that preference by the order it feeds sources in.
function mergeIdentity(base: TokenIdentity, next: Partial<TokenIdentity>): TokenIdentity {
  return {
    offeringId: base.offeringId || (next.offeringId ?? ''),
    offeringTitle: base.offeringTitle || (next.offeringTitle ?? ''),
    tokenSymbol: base.tokenSymbol || (next.tokenSymbol ?? ''),
    tokenHex: base.tokenHex || (next.tokenHex ?? ''),
    issuerWalletAddress: base.issuerWalletAddress || (next.issuerWalletAddress ?? ''),
  }
}

// Unparseable / missing dates sort last rather than first, so a row with no date
// never masquerades as the longest-blocked one.
function dateRank(date: string | null | undefined): number {
  const ms = Date.parse(date ?? '')
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms
}

/**
 * A property is identified by its id; a row that somehow arrives without one
 * falls back to its name so those rows group together instead of each becoming
 * a singleton card.
 *
 * Returned as the gap's `key` too, because the container keys "which card is
 * busy" off it — comparing `propertyId` alone made every id-less card match the
 * empty initial value and render permanently disabled.
 */
function propertyKey(row: { propertyId: string; propertyName: string }): string {
  return row.propertyId || row.propertyName
}

export interface BuildTrustlineGapsInput {
  /** Orders the VAISP has parked at `awaiting_external_opt_in`. */
  pendingOptIns: WalletManagementPendingOptIn[]
  /** Every trustline on the wallet, including failed and removed ones. */
  trustlines: WalletManagementTrustline[]
}

/**
 * One row per property the VAISP is waiting on an opt-in for and the wallet
 * holds no working trustline record for, longest-blocked first.
 */
export function buildTrustlineGaps({
  pendingOptIns,
  trustlines,
}: BuildTrustlineGapsInput): TrustlineGap[] {
  // ── Token identity per property, best source first ──
  //
  // The two sources agree on what they are reading: the pending opt-in joins
  // `vasp_offerings.vasp_token_hex` / `.vasp_issuer_address`, and the trustline
  // row is joined through the same property to the same columns. The opt-in goes
  // first because it names the offering the order was actually placed against.
  const identities = new Map<string, TokenIdentity>()

  const remember = (key: string, next: Partial<TokenIdentity>) => {
    identities.set(key, mergeIdentity(identities.get(key) ?? EMPTY_TOKEN_IDENTITY, next))
  }

  // Grouping by property is safe to act on because a property carries exactly
  // one primary-market offering, so whichever source supplies the offering id
  // supplies the only one an order could be against. Revisit this if secondary
  // offerings ever reach this screen.
  for (const optIn of pendingOptIns) remember(propertyKey(optIn), optIn)
  for (const trustline of trustlines) remember(propertyKey(trustline), trustline)

  // ── The wallet's own trustline record per property ──
  // The authoritative half of the rule. An opt-in row carries the same join, but
  // only the trustline list distinguishes "no row" from "a row we never saw".
  const walletTrustlineStatus = new Map<string, TrustlineStatus>()
  // Issuer authorisation travels with the row that decided the status, so the
  // badge and the authorisation line on a card always describe the same
  // trustline. A property with no row at all is absent from this map, and the
  // card then reads `undefined` → treated as "nothing known", same as null.
  const walletPeerAuthorized = new Map<string, boolean | null>()
  for (const trustline of trustlines) {
    const key = propertyKey(trustline)
    const current = walletTrustlineStatus.get(key)
    const isMoreAlive =
      !current ||
      (TRUSTLINE_LIVENESS[trustline.trustlineStatus] ?? -1) > (TRUSTLINE_LIVENESS[current] ?? -1)
    if (isMoreAlive) {
      walletTrustlineStatus.set(key, trustline.trustlineStatus)
      walletPeerAuthorized.set(key, trustline.peerAuthorized)
    }
  }

  /**
   * The one status both the include test and the badge read.
   *
   * The wallet's own record decides, falling back to the status the opt-in row
   * reports only where the wallet list has nothing for that property. The
   * fallback direction is deliberate: no record means "not set up", not
   * "probably fine" — over-reporting costs a redundant click, under-reporting
   * costs a blocked DLD trade registration.
   */
  const effectiveStatus = (key: string, payloadStatus: TrustlineStatus | null) =>
    walletTrustlineStatus.get(key) ?? payloadStatus

  const groups = new Map<string, TrustlineGap>()

  for (const optIn of pendingOptIns) {
    const key = propertyKey(optIn)
    const status = effectiveStatus(key, optIn.trustlineStatus)
    // The second half of the rule. A property the wallet already trusts is not a
    // trustline problem — if its order is still parked, that is an order
    // problem, and the orders screen is where it is answered.
    if (!needsTrustlineWork(status)) continue

    const group = groups.get(key)
    if (group) {
      // Several orders can wait on one property's token, but they all wait on
      // the same single trustline — so they collapse to one card, dated by
      // whichever has been blocked longest.
      if (dateRank(optIn.orderedAt) < dateRank(group.blockedSince)) {
        group.blockedSince = optIn.orderedAt
      }
      continue
    }

    groups.set(key, {
      key,
      propertyId: optIn.propertyId,
      propertyName: optIn.propertyName || 'Unnamed property',
      blockedSince: optIn.orderedAt,
      ...EMPTY_TOKEN_IDENTITY,
      trustlineStatus: status,
      // `?? null`, never `?? false`: a property with no trustline row has
      // nothing to authorise and nothing was read, so the card says "not
      // checked" rather than claiming the issuer declined.
      peerAuthorized: walletPeerAuthorized.get(key) ?? null,
      requiresTrustlineSetup: requiresSetup(status),
      missingTokenFields: [],
      canSetupTrustline: false,
    })
  }

  for (const [key, gap] of groups) {
    const identity = identities.get(key) ?? EMPTY_TOKEN_IDENTITY
    Object.assign(gap, identity)
    gap.missingTokenFields = missingTokenFields(identity)
    // Completeness is the whole test: whichever source supplied the offering,
    // issuer and currency code, they name the same columns the TrustSet reads.
    gap.canSetupTrustline = gap.missingTokenFields.length === 0
  }

  return [...groups.values()].sort((a, b) => dateRank(a.blockedSince) - dateRank(b.blockedSince))
}
